import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <div className="paper-panel-elevated max-w-md text-center px-8 py-10">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-lg font-semibold text-ink mb-2">
              页面出错了
            </h2>
            <p className="text-sm text-ink-secondary mb-6 leading-relaxed">
              很抱歉，页面遇到了一个意外错误。您可以尝试刷新页面或返回首页。
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 rounded-lg bg-primary-700 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                重试
              </button>
              <button
                onClick={() => window.location.href = "/"}
                className="px-4 py-2 rounded-lg border border-border bg-surface text-ink-secondary text-sm font-medium hover:bg-surface-sunken transition-colors"
              >
                返回首页
              </button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-ink-tertiary cursor-pointer hover:text-ink-secondary">
                  查看错误详情
                </summary>
                <pre className="mt-3 p-3 bg-surface-sunken rounded text-[10px] text-negative overflow-auto max-h-32">
                  {this.state.error.message}
                  {"\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
